<div class="handle-consent-simplified">
  <div class="stepper-container" #stepperContainer>
    <custom-stepper #stepper [linear]="false" (selectionChange)="onStepChange($event)">
      <!-- Step 1: Confirm Meters -->
      <custom-step stepId="meters" label="Confirm Meters" [completed]="stepConfigs()[0].isValid()" [hasError]="hasCriticalMeterError()">
        <div class="step-content">
          <h2>Meters to Add to Your Wallet</h2>

          @if (isMetersLoading()) {
          <div class="loading-section">
            <mat-spinner diameter="40"></mat-spinner>
            <span>Loading available meters...</span>
          </div>
          } @else if (meterDetailsError()) {
          <div class="error-message meter-details-error">
            <mat-icon>error</mat-icon>
            <div class="error-content">
              <h3>Meter Details Invalid</h3>
              <p>Unable to fetch meter details. Please try again with correct meter information.</p>
              <p><small>Cannot proceed without valid meter data.</small></p>
            </div>
          </div>
          } @else if (allMetersAlreadyAdded()) {
          <div class="info-message">
            <mat-icon>info</mat-icon>
            <p>
              All requested meters are already in your wallet. You can proceed
              to share consent directly.
            </p>
          </div>
          } @else if (availableMeters().length === 0) {
          <div class="no-meters-message">
            <mat-icon>info_outline</mat-icon>
            <h3>No New Meters Found</h3>
            <p>All requested meters are already available in your wallet.</p>
          </div>
          } @else {
          <p>
            The following {{ availableMeters().length }} meter(s) will be added
            to your wallet:
          </p>

          <div class="meters-container">
            @if (shouldUseVirtualScrollingForCards()) {
              <cdk-virtual-scroll-viewport [itemSize]="meterCardItemSize" [style.height.px]="meterCardViewportHeight()"
                class="meters-viewport">
                <div class="meter-card" *cdkVirtualFor="
                    let meter of availableMeters();
                    trackBy: trackMeter
                  ">
                  <div class="meter-header">
                    <div class="meter-number">
                      <!-- <mat-icon>electric_meter</mat-icon> -->
                      <span class="meter-no">{{ meter.meterNo }}</span>
                    </div>
                    <div class="fuel-type-badge" [class]="'fuel-type-' + meter.fuelType.toLowerCase()">
                      {{ meter.fuelType }}
                    </div>
                  </div>
                  <div class="meter-body">
                    <div class="address-section">
                      <mat-icon class="address-icon">location_on</mat-icon>
                      <span class="address-text">{{ meter.addressAsLine }}</span>
                    </div>
                  </div>
                </div>
              </cdk-virtual-scroll-viewport>
            } @else {
              <!-- Regular cards without virtual scrolling for small datasets -->
              <div class="meters-cards-container">
                <div class="meter-card" *ngFor="
                    let meter of availableMeters();
                    trackBy: trackMeter
                  ">
                  <div class="meter-header">
                    <div class="meter-number">
                      <!-- <mat-icon>electric_meter</mat-icon> -->
                      <span class="meter-no">{{ meter.meterNo }}</span>
                    </div>
                    <div class="fuel-type-badge" [class]="'fuel-type-' + meter.fuelType.toLowerCase()">
                      {{ meter.fuelType }}
                    </div>
                  </div>
                  <div class="meter-body">
                    <div class="address-section">
                      <mat-icon class="address-icon">location_on</mat-icon>
                      <span class="address-text">{{ meter.addressAsLine }}</span>
                    </div>
                  </div>
                </div>
              </div>
            }
          </div>
          }
        </div>
      </custom-step>

      <!-- Step 2: Relationship Type -->
      <custom-step [hidden]="!stepConfigs()[1].isVisible()" stepId="relationship" label="Relationship Type"
        [completed]="stepConfigs()[1].isValid()" [hasError]="false">
        <div class="step-content">
          <h2>Your Relationship to the Property</h2>
          <p>
            Please select your relationship to the property at the meter
            address:
          </p>

          <app-custom-radio-button [options]="relationshipOptions()" [ngModel]="relationshipType()"
            (ngModelChange)="onRelationshipSelected($event)">
          </app-custom-radio-button>
        </div>
      </custom-step>

      <!-- Step 3: Consent Expiry -->
      <custom-step [hidden]="!stepConfigs()[2].isVisible()" stepId="expiry" label="Consent Expiry"
        [completed]="stepConfigs()[2].isValid()" [hasError]="false">
        <div class="step-content">
          <h2>Consent Expiry Date</h2>
          <p>When should this consent expire?</p>

          <mat-form-field appearance="outline" class="expiry-field">
            <mat-label>Expiry Date</mat-label>
            <input matInput [matDatepicker]="picker" [ngModel]="expiryDate()" (ngModelChange)="setExpiryDate($event)"
              [min]="minExpiryDate" [max]="maxExpiryDate" />
            <mat-datepicker-toggle matSuffix [for]="picker"></mat-datepicker-toggle>
            <mat-datepicker #picker></mat-datepicker>
            <mat-hint>Must be between {{ minExpiryDate | date }} and
              {{ maxExpiryDate | date }}</mat-hint>
          </mat-form-field>
        </div>
      </custom-step>

      <!-- Step 4: Granularity -->
      <custom-step [hidden]="!stepConfigs()[3].isVisible()" stepId="granularity" label="Data Granularity"
        [completed]="stepConfigs()[3].isValid()" [hasError]="false">
        <div class="step-content">
          <h2>Data Access Granularity</h2>
          <p>
            At what level of detail would you like to provide access to your
            meter data?
          </p>

          <app-custom-radio-button [options]="granularityOptions" [ngModel]="granularity()"
            (ngModelChange)="onGranularitySelected($event)">
          </app-custom-radio-button>
        </div>
      </custom-step>

      <!-- Step 5: Smart Meter Agreement -->
      <custom-step [hidden]="!stepConfigs()[4].isVisible()" stepId="smartMeterAgreement" label="Smart Meter Agreement"
        [completed]="stepConfigs()[4].isValid()" [hasError]="false">
          @if (stepConfigs()[4].isVisible()) {
            <div class="step-content">
              <h2>Smart Meter Agreement</h2>
              
              <app-smart-meter-agreement [ngModel]="smartMeterAgreementControl.value"
                (ngModelChange)="onSmartMeterTermsAgreed($event)" [selectedMeters]="availableMeters()">
              </app-smart-meter-agreement>
            </div>
        }
      </custom-step>

      <!-- Step 6: Add Consent -->
      <custom-step [hidden]="!stepConfigs()[5].isVisible()" stepId="addConsent" label="Add Consent"
        [completed]="stepConfigs()[5].isValid()" [hasError]="validationError() !== ''">
        @if (stepConfigs()[5].isVisible()) {
          <div class="step-content">
            <h2>Provide Consent</h2>

            @switch (computedConsentCategory()) { @case ('CHECKBOX') {
            <app-add-nhh-consent [ngModel]="consentControl.value" (ngModelChange)="onConsentChange($event)">
            </app-add-nhh-consent>
            } @case ('LOA') { @if (relationshipType()) {
            <app-add-hh-consent [ngModel]="consentControl.value" (ngModelChange)="onConsentChange($event)"
              [relationshipType]="relationshipType()!" [selectedMeters]="availableMeters()">
            </app-add-hh-consent>
            } } @case ('SMART_METER') {
              @if (smartMeterAgreementControl.valid) {
                <app-smart-consent-options [useRadioButtons]="true"
                  [supportedSmartMeterConsentTypes]="supportedSmartMeterConsentTypes"
                  [moveInDate]="smartMeterAgreementControl.value?.moveInDate || null"
                  [termsAccepted]="
                    smartMeterAgreementControl.value?.termsAccepted || false
                  " [ngModel]="consentDataControl.value" (ngModelChange)="onConsentDataChange($event)"
                  [selectedMeters]="availableMeters()">
                </app-smart-consent-options>
              }
            } @default {
            <p>Other consent types will be handled here.</p>
            } } @if (validationError()) {
            <div class="error-message">{{ validationError() }}</div>
            } @if (validationSuccess()) {
            <div class="success-message">
              Consent validated and verified successfully!
            </div>
            }
          </div>
        }
      </custom-step>

      <!-- Step 7: Share Consent (revamped) -->
      <custom-step stepId="shareConsent" label="Share Consent" [completed]="stepConfigs()[6].isValid()"
        [hasError]="false" [hidden]="!stepConfigs()[6].isVisible()">
        <div class="step-content share-consent-step">
          <h2>Share Your Consent</h2>
          <p class="org-intro">
            The software platform
            <span class="org-highlight">{{ requestingOrganization()?.software }}</span>
            is requesting access to your meter data.
          </p>
          <div class="meters-summary unified">
            <h3>
              Meters to Share ({{
              allMetersAlreadyAdded()
              ? allRequestedMeters().length
              : availableMeters().length
              }}):
            </h3>
            <!-- Use virtual scroll only for larger datasets -->
            @if (shouldUseVirtualScrolling()) {
              <cdk-virtual-scroll-viewport 
                [itemSize]="meterTableRowHeight" 
                [style.height.px]="meterTableViewportHeight()"
                class="meters-table-viewport">
                <table class="meters-table">
                  <thead>
                    <tr>
                      <th>Meter</th>
                      <!-- <th>Fuel Type</th> -->
                      <th>Address</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr *cdkVirtualFor="
                        let meter of allMetersAlreadyAdded()
                          ? allRequestedMeters()
                          : availableMeters();
                        trackBy: trackMeter
                      ">
                      <td>{{ meter.meterNo }}</td>
                      <!-- <td>
                        <span class="fuel-badge" [class]="'fuel-type-' + meter.fuelType.toLowerCase()">
                          {{ meter.fuelType }}
                        </span>
                      </td> -->
                      <td>{{ meter.addressAsLine }}</td>
                    </tr>
                  </tbody>
                </table>
              </cdk-virtual-scroll-viewport>
            } @else {
              <!-- Regular table without virtual scrolling for small datasets -->
              <div class="meters-table-container">
                <table class="meters-table">
                  <thead>
                    <tr>
                      <th>Meter</th>
                      <!-- <th>Fuel Type</th> -->
                      <th>Address</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr *ngFor="
                        let meter of allMetersAlreadyAdded()
                          ? allRequestedMeters()
                          : availableMeters();
                        trackBy: trackMeter
                      ">
                      <td>{{ meter.meterNo }}</td>
                      <!-- <td>
                        <span class="fuel-badge" [class]="'fuel-type-' + meter.fuelType.toLowerCase()">
                          {{ meter.fuelType }}
                        </span>
                      </td> -->
                      <td>{{ meter.addressAsLine }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            }
          </div>
        </div>
      </custom-step>

      <!-- Step 8: Confirmation -->
      <custom-step [hidden]="!stepConfigs()[7].isVisible()" stepId="confirmation" label="Confirmation" [completed]="true" [hasError]="false">
        <div class="step-content confirmation-step">
          <div class="confirmation-icon">
            @if (consentDecision() === 'approved') {
            <mat-icon class="success-icon">check_circle</mat-icon>
            } @else {
            <mat-icon class="decline-icon">cancel</mat-icon>
            }
          </div>

          <h2>
            @if (consentDecision() === 'approved') { Consent Shared Successfully
            } @else { Consent Request Declined }
          </h2>

          @if (consentDecision() === 'approved') {
          <p>
            Your consent has been successfully shared with
            {{ requestingOrganization() }}.
          </p>
          @if (consentReference()) {
          <div class="reference-info">
            <strong>Reference:</strong> {{ consentReference() }}
          </div>
          } } @else {
          <p>
            You have declined to share your consent with
            {{ requestingOrganization() }}.
          </p>
          }
        </div>
      </custom-step>
    </custom-stepper>
  </div>

  <!-- Navigation Controls -->
  <div class="navigation-container">
    <!-- <button (click)="scrollToEnd()">Scroll test</button> -->
    @if (!isConfirmationStep()) { @if (!isShareConsentStep()) {
    <div class="stepper-navigation">
      <button mat-stroked-button (click)="previousStep()" [disabled]="isFirstStep()">
        Previous
      </button>
      @if (isNextButtonLoading()) {
      <button mat-flat-button color="primary" disabled>
        <!-- <mat-spinner diameter="20" color="accent"></mat-spinner> -->
        Please wait...
      </button>
      } @else {
      <button mat-flat-button color="primary" (click)="nextStep()" [disabled]="!canProceed()">
        {{ nextButtonLabel() }}
      </button>
      }
    </div>
    } @else {
    <div class="share-actions">
      <button mat-button color="warn" class="decline-btn" [disabled]="isSharing()"
        (click)="makeConsentDecision('decline')">
        <mat-icon>close</mat-icon>
        Decline
      </button>
      <button mat-flat-button color="primary" class="approve-btn" [disabled]="isSharing()"
        (click)="makeConsentDecision('approve')">
        @if (!isSharing()) {
        <mat-icon>check</mat-icon>
        } @if (isSharing()) { Sharing... } @else { Approve & Share }
      </button>
    </div>
  } }
  </div>
</div>
